import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, ConfirmDeleteModal, SearchInput } from '@/components';
import { V2Table } from '@/common/components';
import { PlusIcon } from '../../components/Icons';
// import exportIcon from '../../assets/exportIcon.png';  // export
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import {
  getCRMContactNotes,
  createCRMContactNote,
  updateCRMContactNote,
  deleteCRMContactNote,
} from '../api';
import { parseApiError } from '@/common/utils';
import { pageWindow } from '../utils/pagedList';

const PAGE_SIZE = 10;

// const toolbarBtnClass =
//   'inline-flex h-[44px] items-center justify-center gap-[6px] rounded-[12px] px-4 text-sm sm:text-[15px] font-medium font-sf-pro-display text-[#1F2241] transition-colors focus:outline-none hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed';     // export
export function CRMNotesSection({
  companyId,
  contactId,
  readOnly = false,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) {
  const queryClient = useQueryClient();
  const [noteBody, setNoteBody] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalBody, setOriginalBody] = useState('');
  const [noteToDelete, setNoteToDelete] = useState(null);

  const isPreviewing = !!activeNoteId && !isEditMode;

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: notesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'crm-contact-notes',
      companyId,
      contactId,
      page,
      debouncedSearch,
    ],
    queryFn: () =>
      getCRMContactNotes({
        companyId,
        contactId,
        params: {
          page,
          page_size: PAGE_SIZE,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      }),
    enabled: !!companyId && !!contactId,
  });

  const { rows: notes, totalCount } = pageWindow(notesData, page, PAGE_SIZE);

  const invalidateNotes = () => {
    queryClient.invalidateQueries({
      queryKey: ['crm-contact-notes', companyId, contactId],
    });
  };

  const { mutate: handleAddNote, isPending: isAdding } = useMutation({
    mutationFn: (vars) => {
      if (!canCreate) throw new Error('Permission denied.');
      return createCRMContactNote(vars);
    },
    onSuccess: () => {
      toast.success('Note added successfully!');
      setNoteBody('');
      setPage(1);
      invalidateNotes();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to add note.');
    },
  });

  const { mutate: handleUpdateNote, isPending: isUpdating } = useMutation({
    mutationFn: (vars) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return updateCRMContactNote(vars);
    },
    onSuccess: () => {
      toast.success('Note updated successfully!');
      setActiveNoteId(null);
      setIsEditMode(false);
      setNoteBody('');
      setOriginalBody('');
      invalidateNotes();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update note.');
    },
  });

  const { mutate: handleDeleteNote } = useMutation({
    mutationFn: (vars) => {
      if (!canDelete) throw new Error('Permission denied.');
      return deleteCRMContactNote(vars);
    },
    onSuccess: () => {
      toast.success('Note deleted successfully!');
      if (noteToDelete === activeNoteId) {
        setActiveNoteId(null);
        setIsEditMode(false);
        setNoteBody('');
        setOriginalBody('');
      }
      setNoteToDelete(null);
      invalidateNotes();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete note.');
    },
  });

  const submitAddNote = () => {
    if (!noteBody.trim() || readOnly || isPreviewing || !canCreate) return;
    handleAddNote({ companyId, contactId, payload: { body: noteBody } });
  };

  const isDirty = isEditMode && noteBody !== originalBody;

  const confirmDiscard = () => {
    if (!isDirty) return true;
    return window.confirm('Discard unsaved changes to this note?');
  };

  const resetEditor = () => {
    setActiveNoteId(null);
    setIsEditMode(false);
    setNoteBody('');
    setOriginalBody('');
  };

  const startEditing = (note) => {
    if (!canUpdate || isUpdating) return;
    if (activeNoteId === note.id && isEditMode) return;
    if (!confirmDiscard()) return;
    setActiveNoteId(note.id);
    setIsEditMode(true);
    setNoteBody(note.body || '');
    setOriginalBody(note.body || '');
  };

  const cancelEditing = () => {
    if (isUpdating) return;
    if (!confirmDiscard()) return;
    resetEditor();
  };

  const saveEdit = () => {
    if (!noteBody.trim() || !activeNoteId || !canUpdate || isUpdating) return;
    handleUpdateNote({
      companyId,
      contactId,
      noteId: activeNoteId,
      payload: { body: noteBody },
    });
  };

  useEffect(() => {
    if (!isEditMode) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      cancelEditing();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEditMode, isDirty, isUpdating, noteBody, originalBody]);

  const showComposer =
    !readOnly && (isEditMode ? canUpdate : canCreate || isPreviewing);

  const columns = [
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 160,
      minWidth: 120,
      cell: (note) => (
        <span className="truncate block text-sm text-[#1E293B]">
          {note.author_name || 'User'}
        </span>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      defaultWidth: 360,
      minWidth: 180,
      cell: (note) => (
        <span
          className="line-clamp-2 break-words text-sm text-[#1E293B]"
          title={note.body || ''}
        >
          {note.body}
        </span>
      ),
    },
    {
      id: 'created',
      label: 'Created',
      defaultWidth: 170,
      minWidth: 140,
      cell: (note) => (
        <span className="truncate block text-sm text-[#1E293B]">
          {formatDateTime(note.created_at)}
        </span>
      ),
    },
    {
      id: 'updated',
      label: 'Updated',
      defaultWidth: 170,
      minWidth: 140,
      cell: (note) => (
        <span className="truncate block text-sm text-[#1E293B]">
          {formatDateTime(note.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      label: 'Action',
      defaultWidth: 100,
      minWidth: 88,
      align: 'center',
      stopPropagation: true,
      cell: (note) =>
        !readOnly && (canUpdate || canDelete) ? (
          <div className="flex items-center justify-end gap-1">
            {canUpdate && (
              <button
                type="button"
                onClick={() => startEditing(note)}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] transition-colors focus:outline-none"
                title="Edit Note"
              >
                <img src={editIcon} alt="" className="w-4 h-4 object-contain" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => setNoteToDelete(note.id)}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#FEEFEF] transition-colors focus:outline-none"
                title="Delete Note"
              >
                <img
                  src={trashIcon}
                  alt=""
                  className="w-4 h-4 object-contain"
                />
              </button>
            )}
          </div>
        ) : null,
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-3 overflow-hidden">
      {/* Add / Edit Note input */}
      {showComposer && (
        <div className="note-editor flex shrink-0 flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-base font-semibold text-[#1F2241]">
              {isEditMode ? 'Edit Note' : 'Add Note'}
            </h4>
          </div>
          <textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder="Enter Note Here..."
            rows={4}
            disabled={
              isAdding ||
              isUpdating ||
              readOnly ||
              (isEditMode ? !canUpdate : !canCreate)
            }
            className="w-full px-4 py-3 rounded-[12px] bg-[#FFFBE6] border border-[#F4E7A8] text-[#1F2241] text-sm font-sf-pro-display placeholder:text-[#868686] focus:outline-none focus:border-[#E5D48B] focus:ring-1 focus:ring-inset focus:ring-[#E5D48B]/50 transition-all resize-none disabled:opacity-60"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 w-full shrink-0">
        <SearchInput
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search by note or author"
          className="w-full sm:max-w-md sm:flex-1 min-w-0"
          inputClassName="h-[44px]"
        />

        {isEditMode && canUpdate ? (
          <div className="note-edit-actions flex items-center justify-end gap-2 shrink-0">
            <Button
              variant="secondary"
              onClick={cancelEditing}
              disabled={isUpdating}
              className="!bg-white"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={saveEdit}
              disabled={isUpdating || !noteBody.trim()}
              className="w-auto"
            >
              <span className="whitespace-nowrap">
                {isUpdating ? 'Updating...' : 'Update Note'}
              </span>
            </Button>
          </div>
        ) : (
          !readOnly &&
          canCreate && (
            <Button
              variant="primary"
              onClick={submitAddNote}
              disabled={isAdding || !noteBody.trim()}
              className="h-[44px] px-4 w-full sm:w-auto shrink-0"
              icon={<PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
            >
              <span className="whitespace-nowrap">
                {isAdding ? 'Adding...' : 'Add Note'}
              </span>
            </Button>
          )
        )}
      </div>

      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
        <V2Table
          tight
          columns={columns}
          data={isError && !notesData ? [] : notes}
          rowKey="id"
          storageKey="crm_contact_notes_column_widths"
          loading={isLoading || (!notesData && !isError)}
          loadingRowCount={PAGE_SIZE}
          notice={
            isError && !notesData
              ? parseApiError(error)?.message || 'Failed to load notes'
              : null
          }
          empty="No notes found."
          onRowClick={canUpdate && !readOnly ? startEditing : undefined}
          isRowSelected={(note) => activeNoteId === note.id}
          pagination={{
            currentPage: page,
            totalCount,
            itemsPerPage: PAGE_SIZE,
            onChange: setPage,
          }}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={!!noteToDelete}
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
        onCancel={() => setNoteToDelete(null)}
        onConfirm={() =>
          handleDeleteNote({ companyId, contactId, noteId: noteToDelete })
        }
      />
    </div>
  );
}
